import { memo } from 'react';
import styles from '../../styles/WeaponPlate.module.css';
import clsx from '../../utils/clsx';
import WeaponIcon from '../WeaponIcon';
import { ammoBand, ammoColor, magazineFits } from '../../engine/ammoRamp';
import type { Ammo } from '../../types';

interface AmmoMeterProps {
  ammo: Ammo;
  /** Show the reload action beside the count. Only worth it when expanded. */
  showReload?: boolean;
}

/**
 * A magazine, drawn.
 *
 * Rounds as pips while there are few enough of them to count, and a
 * proportional bar above that - 28 bolter rounds read beautifully as
 * individual marks, a 250-round heavy bolter belt reads as a smear.
 * `magazineFits` owns that threshold.
 *
 * THE COUNT IS ALWAYS IN TEXT beside it. Shape and colour are never the
 * only channel (see the reduced-motion rule in code-conventions.md), and
 * "how many rounds exactly" is a question a GM asks constantly.
 */
function AmmoMeter({ ammo, showReload }: AmmoMeterProps) {
  const max = ammo?.max ?? 0;
  const current = Math.max(0, Math.min(ammo?.current ?? 0, max));
  const band = ammoBand(current, max);
  // Engine-computed and applied inline - the documented exception to the
  // no-inline-colours rule, same as healthRamp and condColor. The colour
  // GLIDES with the fill: gold, through amber at the quarter mark, to red.
  const fill = ammoColor(current, max);

  // A weapon that takes no ammunition has no magazine to draw.
  if (max <= 0) return null;

  return (
    <div className={styles.ammo} data-testid="ammo-meter">
      {magazineFits(max) ? (
        <div className={styles.pips} aria-hidden="true">
          {Array.from({ length: max }, (_, i) => (
            <span
              key={i}
              className={clsx(styles.pip, i < current && styles.pipLoaded)}
              style={i < current && fill ? { background: fill } : undefined}
            />
          ))}
        </div>
      ) : (
        <div className={styles.bar} aria-hidden="true">
          <span
            className={styles.barFill}
            style={{ width: `${(current / max) * 100}%`, background: fill ?? undefined }}
          />
        </div>
      )}
      <span className={clsx(styles.ammoCount, styles[band])}>
        {current}/{max}
      </span>
      {showReload && ammo.reloadAction && (
        <span className={styles.reload} title={`${ammo.reloadAction} action to reload`}>
          <WeaponIcon name="reload" size={10} />
          {ammo.reloadAction}
        </span>
      )}
    </div>
  );
}

export default memo(AmmoMeter);
